/* eslint-disable react/prop-types */
import React from 'react';
import Downshift from 'downshift';
import { matchSorter } from 'match-sorter';
import {
  Wrapper,
  InputWrapper,
  SelectedItemsWrapper,
  CloseButton,
  Menu,
  SearchInput,
  Item,
  MenuWrapper,
} from './styles';
import { SvgChevron } from '../../svg';
import { useTheme } from '../../theme';
import Pill from '../Pill';
import { Text } from '../../typography';

function MultiSelect(props) {
  const theme = useTheme();
  const {
    items,
    itemToString: oldItemToString = (i) => i,
    placeholder = 'Select a value',
    searchKeys = [],
    onChange,
    initialSelectedItems,
    selectedItems,
  } = props;

  const [internalSelectedItems, setInternalSelectedItems] = React.useState(
    initialSelectedItems ?? selectedItems
  );

  React.useEffect(() => {
    if (Array.isArray(selectedItems)) {
      setInternalSelectedItems(selectedItems);
    }
  }, [selectedItems]);

  const itemToString = (v) => {
    if (v != null) {
      return oldItemToString(v);
    }
    return '';
  };

  const inputRef = React.useRef();

  React.useEffect(() => {
    if (!selectedItems) {
      onChange(internalSelectedItems);
    }
  }, [selectedItems, internalSelectedItems, onChange]);

  const stateReducer = (state, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          highlightedIndex: state.highlightedIndex,
          isOpen: true,
          inputValue: '',
        };
      default:
        return {
          ...changes,
          inputValue: changes.inputValue ?? state.inputValue,
        };
    }
  };

  function removeItem(item) {
    if (selectedItems) {
      onChange(selectedItems.filter((i) => i !== item));
    } else {
      setInternalSelectedItems((ii) => ii.filter((i) => i !== item));
    }
  }

  function addSelectedItem(item) {
    if (selectedItems) {
      onChange([...selectedItems, item]);
    } else {
      setInternalSelectedItems((ii) => [...ii, item]);
    }
  }

  const getRemoveButtonProps = ({ onClick, item, ...rest } = {}) => {
    return {
      onClick: (e) => {
        if (onClick) onClick(e);
        e.stopPropagation();
        removeItem(item);
      },
      ...rest,
    };
  };

  const isInputFocused = () => document.activeElement === inputRef.current;

  return (
    <Downshift
      {...props}
      stateReducer={stateReducer}
      onChange={addSelectedItem}
      initialInputValue=""
      selectedItem=""
    >
      {({
        getInputProps,
        getToggleButtonProps,
        getMenuProps,
        getRootProps,
        isOpen,
        inputValue,
        getItemProps,
        highlightedIndex,
        toggleMenu,
      }) => (
        <Wrapper
          {...getRootProps({
            onClick: (e) => {
              // toggleMenu();
              e.stopPropagation();
              if (!isOpen) inputRef.current.focus();
            },
          })}
          isMenuOpen={isOpen}
          isFocused={isInputFocused() || isOpen}
        >
          <InputWrapper
            role="button"
            tabIndex={0}
            onClick={(e) => {
              toggleMenu();
              e.stopPropagation();
              if (!isOpen) inputRef.current.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                toggleMenu();
                e.stopPropagation();
              }
            }}
          >
            <SelectedItemsWrapper>
              {internalSelectedItems.length === 0 && !isInputFocused() && (
                <Text style={{ opacity: 0.6 }} noMargin>
                  {placeholder}
                </Text>
              )}
              {internalSelectedItems.length > 0 &&
                internalSelectedItems.map((item, i) => {
                  const { onClick } = getRemoveButtonProps({ item });
                  return (
                    <Pill
                      style={{ margin: '3px 5px 3px 0' }}
                      type="dark"
                      size="small"
                      key={i}
                      onRemove={onClick}
                    >
                      {itemToString(item)}
                    </Pill>
                  );
                })}
              <SearchInput
                visuallyHidden={!isInputFocused()}
                {...getInputProps({
                  ref: inputRef,
                  value: inputValue,
                  onKeyDown(e) {
                    if (e.key === 'Backspace' && !inputValue) {
                      removeItem(internalSelectedItems[internalSelectedItems.length - 1]);
                      e.stopPropagation();
                    }
                  },
                })}
              />
            </SelectedItemsWrapper>
            <CloseButton
              type="button"
              {...getToggleButtonProps({
                // prevents the menu from immediately toggling
                // closed (due to our custom click handler above).
                onClick(e) {
                  e.stopPropagation();
                },
                onKeyDown(e) {
                  e.stopPropagation();
                },
              })}
            >
              <SvgChevron stroke={theme.colors.primary20b} direction={isOpen ? 'top' : 'bottom'} />
            </CloseButton>
          </InputWrapper>
          <MenuWrapper {...getMenuProps()}>
            {isOpen ? (
              <Menu>
                {matchSorter(items, inputValue, { keys: [itemToString, ...searchKeys] })
                  .filter((i) => !internalSelectedItems.map(itemToString).includes(itemToString(i)))
                  .map((item, index) => (
                    <Item
                      highlighted={highlightedIndex === index}
                      key={index}
                      {...getItemProps({
                        item,
                        index,
                      })}
                    >
                      {itemToString(item)}
                    </Item>
                  ))}
              </Menu>
            ) : null}
          </MenuWrapper>
        </Wrapper>
      )}
    </Downshift>
  );
}

export default MultiSelect;
